import styled, { createGlobalStyle } from 'styled-components';
import theme from 'styled-theming';

const backgroundColorThead = theme('name', {
  light: 'rgba(242,242,242,255)',
  dark: 'rgba(70,70,70,255)',
});

const backgroundColorTh = theme('name', {
  light: 'rgb(242, 242, 242)',
  dark: '#FFFFFF17',
});

const cardBorder = theme('name', {
  light: 'rgba(28, 28, 28, 0.12)',
  dark: 'rgba(255, 255, 255, 0.15)',
});

const tableBorder = theme('name', {
  light: 'rgba(28, 28, 28, 0.14)',
  dark: '#504945',
});

const filterActiveBackground = theme('name', {
  light: 'rgba(28, 28, 28, 0.72)',
  dark: 'rgba(255, 255, 255, 0.22)',
});

const filterPopupBackground = theme('name', {
  light: '#ffffff',
  dark: '#2b2b2b',
});

const filterPopupTextColor = theme('name', {
  light: '#1c1c1c',
  dark: '#ffffff',
});

const filterPopupBorderColor = theme('name', {
  light: 'rgba(28, 28, 28, 0.2)',
  dark: '#504945',
});

const filterIconColor = theme('name', {
  light: 'rgba(28, 28, 28, 0.72)',
  dark: 'rgba(255, 255, 255, 0.82)',
});

const filterIconHoverBackground = theme('name', {
  light: 'rgba(28, 28, 28, 0.1)',
  dark: 'rgba(255, 255, 255, 0.16)',
});

const blocksTableHeadBackground = theme('name', {
  light: '#f2f2f2',
  dark: '#4a4a4a',
});

const blocksTableHeadTextColor = theme('name', {
  light: '#1c1c1c',
  dark: '#ececec',
});

const blocksTableEvenRowBackground = theme('name', {
  light: '#f2f2f2',
  dark: 'rgba(255, 255, 255, 0.06)',
});

const blocksTableCellBorderColor = theme('name', {
  light: 'rgba(28, 28, 28, 0.14)',
  dark: 'rgba(255, 255, 255, 0.16)',
});

const blocksTableCellTextColor = theme('name', {
  light: '#1c1c1c',
  dark: '#f3f3f3',
});

export const FlexDealsContainer = styled.div`
  /* Keep sticky headers and filters below modal backdrops without changing their local order. */
  isolation: isolate;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;

  /* ── Filters card ── */
  .filters-card {
    background: ${(props) => props.theme.background.primary};
    border-radius: 4px;
    border: 1px solid ${cardBorder};
    box-shadow:
      rgba(0, 0, 0, 0.28) 0 1px 3px,
      rgba(0, 0, 0, 0.2) 0 0 1px;
    padding: 10px 15px;
    margin-bottom: 12px;
    overflow: visible;
    flex-shrink: 0;
  }

  .filters-row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-end;
  }

  .filter-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    position: relative;
    z-index: 50;
    flex-shrink: 0;
  }

  .filter-label {
    font-size: 11px;
    font-weight: 600;
    color: ${(props) => props.theme.text.medium};
    white-space: nowrap;
  }

  .filters-view-toggle {
    display: flex;
    align-items: center;
    align-self: flex-end;
    margin-left: auto;
    margin-right: 12px;
    flex-shrink: 0;
  }

  .filters-refresh {
    align-self: flex-end;
    flex-shrink: 0;
  }

  .search-container {
    min-width: 260px;
  }

  .selected-filters-bar {
    display: flex;
    gap: 5px;
    margin-bottom: 10px;
    flex-wrap: wrap;
    flex-shrink: 0;

    .filter {
      display: flex;
      flex-wrap: wrap;
      width: auto;
      border: 1px dashed rgba(145, 158, 171, 0.16);
      padding: 8px;
      gap: 8px;
      border-radius: 8px;
      overflow: hidden;
      align-items: center;
    }

    .display-label {
      font-weight: 600;
      font-size: 13px;
    }

    .display-value {
      display: inline-flex;
      font-size: 12px;
      padding: 0 8px;
      border-radius: 8px;
      min-height: 24px;
      align-items: center;
      gap: 4px;
      line-height: 1;
      vertical-align: middle;
    }

    .display-value span {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      line-height: 1;
    }

    .badge-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 14px;
      height: 14px;
      min-width: 14px;
      padding: 0;
      margin-left: 4px;
      line-height: 1;
      vertical-align: middle;
    }

    .badge-button svg {
      width: 12px;
      height: 12px;
      display: block;

      path {
        fill: #fff;
      }
    }
  }

  /* ── Table wrapper ── */
  .table-wrapper {
    flex: 1;
    min-height: 0;
    position: relative;
    display: flex;
    flex-direction: column;
  }

  .table-container {
    border-radius: 5px 5px 0 0;
    flex: 1;
    min-height: 0;
    overflow: auto;
    scrollbar-width: auto;
    transition: opacity 0.15s ease;

    &.table-loading {
      opacity: 0.4;
      pointer-events: none;
    }

    &::-webkit-scrollbar {
      width: 14px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background-color: ${(props) => props.theme.border.medium};
      border-radius: 5px;
      border: 2px solid transparent;
      background-clip: padding-box;
    }
    &::-webkit-scrollbar-thumb:hover {
      background-color: ${(props) => props.theme.border.strong};
    }
  }

  .shell-table {
    width: max-content;
    min-width: 100%;
    table-layout: fixed;

    thead {
      position: sticky;
      top: 0;
      z-index: 8;
      background-color: ${backgroundColorThead};
    }

    thead th {
      padding: 6px 8px;
      background-color: ${backgroundColorTh};
      border: 1px solid ${tableBorder};
      font-weight: 700;
      white-space: nowrap;
      user-select: none;
      text-align: left;
      vertical-align: middle;
      position: relative;
      overflow: visible;

      &:nth-child(1) {
        width: 90px;
      }
      &:nth-child(2) {
        width: 130px;
      }
      &:nth-child(3) {
        width: 155px;
      }
      &:nth-child(4) {
        width: 130px;
      }
      &:nth-child(5) {
        width: 130px;
      }
      &:nth-child(6) {
        width: 150px;
      }
      &:nth-child(7) {
        width: 150px;
      }
      &:nth-child(8) {
        width: 200px;
      }
      &:nth-child(9) {
        width: 80px;
      }
    }

    tbody td {
      padding: 4px 8px;
      border: 1px solid ${tableBorder};
      font-size: 13px;
      height: 36px;
      vertical-align: middle;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .col-status {
    width: 90px;
    text-align: center !important;
  }

  .col-receipt-time {
    width: 150px;
  }

  .col-reference {
    width: 160px;
  }

  .col-shell-entity {
    width: 130px;
  }

  .col-cpty {
    width: 160px;
  }

  .col-details {
    width: 100px;
    text-align: center !important;
  }

  .col-period-begin {
    width: 130px;
  }

  .col-period-end {
    width: 130px;
  }

  .status-icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .status-success {
    color: #00a650;
  }

  .status-failed {
    color: #d32f2f;
  }

  .status-pending {
    color: #f9a825;
  }

  .empty-cell {
    text-align: center;
    padding: 20px !important;
    color: ${(props) => props.theme.text.medium};
  }

  .load-sentinel {
    height: 1px;
  }

  .loading-more {
    display: flex;
    justify-content: center;
    padding: 10px 0;
  }

  /* ── Sortable column header ── */
  .th-sortable {
    cursor: pointer;
  }

  .th-content {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
  }

  .sort-icon {
    opacity: 0.45;
    flex-shrink: 0;
  }

  /* ── Column header with sort + filter ── */
  .th-cell {
    display: flex;
    align-items: center;
    gap: 4px;
    width: 100%;
  }

  .th-sort-trigger {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    user-select: none;
    flex: 1;
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: inherit;
    text-align: left;

    .sort-icon {
      opacity: 0.45;
    }

    &:hover .sort-icon {
      opacity: 1;
    }
  }

  .th-filter-wrapper {
    position: relative;
    flex-shrink: 0;
  }

  .th-filter-btn {
    background: transparent;
    border: none;
    font: inherit;
    padding: 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${filterIconColor};
    flex-shrink: 0;
    opacity: 0.9;
    margin-left: 4px;
    width: 18px;
    height: 18px;
    border-radius: 3px;

    /* stylelint-disable-next-line no-descending-specificity */
    svg {
      width: 12px;
      height: 12px;
      display: block;
    }

    &:hover {
      opacity: 1;
      background-color: ${filterIconHoverBackground};
    }

    &.active {
      opacity: 1;
      color: #fff;
      background-color: ${filterActiveBackground};
      border-radius: 3px;
      min-width: 18px;
      height: 18px;
      justify-content: center;
      margin-left: 4px;
      padding: 0;

      svg {
        width: 12px;
        height: 12px;
      }
    }
  }

  .col-filter-popup {
    position: absolute;
    top: calc(100% + 4px);
    left: 50%;
    transform: translateX(-50%);
    background: ${(props) => props.theme.background.primary};
    border: 1px solid ${(props) => props.theme.border.medium};
    border-radius: 4px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
    z-index: 100;
    min-width: 210px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .col-filter-clear {
    align-self: flex-end;
    font-size: 11px;
    cursor: pointer;
    background: none;
    border: none;
    color: ${(props) => props.theme.text.link};
    padding: 0;

    &:hover {
      text-decoration: underline;
    }
  }

  /* ── Row count footer ── */
  .row-count {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    font-size: 12px;
    color: ${(props) => props.theme.text.medium};
    border-top: 1px solid ${tableBorder};
  }

  .row-count-loaded {
    margin-left: auto;
  }

  .row-count-total {
    font-weight: 600;
    color: ${(props) => props.theme.text.onSurface};
  }

  .row-count-sep {
    color: ${(props) => props.theme.text.medium};
  }

  /* Popup styles moved to FlexModalGlobalStyle — portal renders outside this scope */
`;

export const FlexModalGlobalStyle = createGlobalStyle`
  .deal-quantity-modal .shell-modal-container {
    margin: 24vh auto 0;
  }

  .deal-quantity-modal .shell-modal-container-content {
    max-height: 68vh;
    overflow: hidden;
  }

  /* Column filter popup — rendered via portal to escape thead stacking context */
  .col-filter-popup-flex {
    position: fixed;
    transform: translateX(-50%);
    background-color: ${filterPopupBackground};
    color: ${filterPopupTextColor};
    border: 1px solid ${filterPopupBorderColor};
    border-radius: 4px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
    opacity: 1;
    isolation: isolate;

    /* This body portal sits above the isolated page, but below modal backdrops. */
    z-index: 1;
    min-width: 210px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .col-filter-popup-flex-wide {
    min-width: 300px;
  }

  .col-filter-input-row-flex {
    display: flex;
    align-items: center;
    gap: 8px;

    .shell-input-wrapper {
      flex: 1;
      min-width: 0;
    }
  }

  .flex-modal-body {
    padding: 16px 0;
  }

  .deal-id {
    font-size: 14px;
    text-align: left;
    margin-bottom: 12px;
    color: ${blocksTableCellTextColor};

    strong {
      font-weight: 700;
    }
  }

  .blocks-loading {
    display: flex;
    justify-content: center;
    padding: 24px 0;
  }

  .blocks-table-scroll {
    overflow-y: auto;
    max-height: calc(68vh - 210px);
  }

  .blocks-table {
    width: 100%;

    thead {
      position: sticky;
      top: 0;
      z-index: 3;
      background: ${blocksTableHeadBackground};
    }

    thead th {
      position: sticky;
      top: 0;
      z-index: 4;
      font-size: 12px;
      font-weight: 700;
      text-align: left;
      padding: 6px 10px;
      color: ${blocksTableHeadTextColor};
      background: ${blocksTableHeadBackground};
      border-bottom: 2px solid ${blocksTableCellBorderColor};
    }

    tbody tr:nth-child(even) td {
      background: ${blocksTableEvenRowBackground};
    }

    /* stylelint-disable-next-line no-descending-specificity */
    tbody td {
      font-size: 13px;
      padding: 6px 10px;
      color: ${blocksTableCellTextColor};
      border-bottom: 1px solid ${blocksTableCellBorderColor};
    }
  }
`;
